// FILE: src/components/CertTrackSwitcher.js
//
// Reusable PMP / CPMAI (and future) certification-track switcher used on the
// dashboard, readiness, study plan, and milestones pages.
//
//   • A cert WITH a baseline renders as a selectable tab (onSelect).
//   • A cert WITHOUT a baseline renders as a dashed "+ <cert>" button that
//     invites the learner to start it (onStartCert) — the caller typically
//     opens a "Set a <cert> baseline" dialog.
//   • A cert flagged `comingSoon` in config/exams.js renders as a disabled
//     "· soon" chip, so adding a future cert (e.g. PMI-ACP) is just a config
//     flip — no page edits needed.

import React from 'react';
import { getAllExams } from '../../config/exams';
import styles from './CertTrackSwitcher.module.css';

export default function CertTrackSwitcher({
  activeCert,
  baselines = {},
  onSelect,
  onStartCert,
  testIdPrefix = 'cert',
  className,
}) {
  const all = getAllExams();
  const active = all.filter((e) => !e.comingSoon);
  const upcoming = all.filter((e) => e.comingSoon);

  // Nothing to switch between — don't render chrome for a single track.
  if (active.length + upcoming.length < 2) return null;

  return (
    <div
      className={`${styles.switcher} ${className || ''}`}
      role="tablist"
      aria-label="Certification track"
      data-testid={`${testIdPrefix}-track-switcher`}
    >
      <span className={styles.label}>Track:</span>

      {active.map((e) => {
        const c = e.examId;
        const label = e.displayName || c.toUpperCase();
        return baselines[c] ? (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={activeCert === c}
            className={`${styles.btn} ${activeCert === c ? styles.btnActive : ''}`}
            onClick={() => onSelect?.(c)}
            data-testid={`${testIdPrefix}-cert-${c}`}
          >
            {label}
          </button>
        ) : (
          <button
            key={c}
            type="button"
            className={`${styles.btn} ${styles.btnStart}`}
            onClick={() => onStartCert?.(c)}
            title={`Start your ${label} track`}
            data-testid={`${testIdPrefix}-cert-start-${c}`}
          >
            + {label}
          </button>
        );
      })}

      {upcoming.map((e) => {
        const c = e.examId;
        const label = e.displayName || c.toUpperCase();
        return (
          <span
            key={c}
            className={`${styles.btn} ${styles.btnSoon}`}
            title={`${label} — coming soon`}
            data-testid={`${testIdPrefix}-cert-soon-${c}`}
          >
            {label} · soon
          </span>
        );
      })}
    </div>
  );
}
